import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useRBAC } from '../hooks/useRBAC';
import { inventoryCategoriesService, InventoryCategory } from '../services/inventoryCategoriesService';

// shadcn/ui components
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Switch } from '../components/ui/switch';

// Lucide React icons
import {
  Plus,
  Edit,
  Search,
  Tag,
  Trash2,
  AlertTriangle,
  Package,
  CheckCircle2,
  XCircle,
} from 'lucide-react';

interface InventoryCategoryListProps {
  refreshTrigger?: number;
  onCreateClick: () => void;
  onEditClick: (category: InventoryCategory) => void;
}

export const ModernInventoryCategoryList: React.FC<InventoryCategoryListProps> = ({
  refreshTrigger,
  onCreateClick,
  onEditClick,
}) => {
  const { translate } = useTranslation();
  const { hasPermission } = useRBAC();

  // Permission checks
  const canCreate = hasPermission('inventory_categories', 'create');
  const canEdit = hasPermission('inventory_categories', 'edit');
  const canDelete = hasPermission('inventory_categories', 'delete');

  const [categories, setCategories] = useState<InventoryCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<InventoryCategory | null>(null);

  const loadCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await inventoryCategoriesService.getInventoryCategories();
      setCategories(data);
    } catch (err) {
      console.error('Failed to load inventory categories:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to load inventory categories'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories, refreshTrigger]);

  const handleDelete = async () => {
    if (!categoryToDelete) return;

    try {
      await inventoryCategoriesService.deleteInventoryCategory(categoryToDelete.id);
      setCategories((prev) =>
        prev.filter((c) => c.id !== categoryToDelete.id)
      );
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
      setError(null);
    } catch (err) {
      console.error('Delete inventory category error:', err);
      const errorMessage = err instanceof Error 
        ? err.message 
        : typeof err === 'object' && err !== null && 'message' in err
        ? String(err.message)
        : 'Failed to delete inventory category';
      setError(errorMessage);
    }
  };

  const filteredCategories = categories.filter((category) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      category.name.toLowerCase().includes(searchLower) ||
      (category.description && category.description.toLowerCase().includes(searchLower))
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Tag className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold">
            {translate('pos.inventoryCategories.name', 'Inventory Categories')}
          </h1>
        </div>
        {canCreate && (
          <Button onClick={onCreateClick} className="flex items-center space-x-2">
            <Plus className="h-4 w-4" />
            <span>{translate('pos.inventoryCategories.add', 'Add Category')}</span>
          </Button>
        )}
      </div>

      {/* Search */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Search className="h-5 w-5" />
            <span>{translate('common.search', 'Search')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={translate(
                'pos.inventoryCategories.search',
                'Search categories...'
              )}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Category Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            {translate('pos.inventoryCategories.list', 'Category List')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredCategories.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchTerm
                ? translate('pos.inventoryCategories.empty', 'No categories found')
                : translate('pos.inventoryCategories.empty', 'No categories yet')}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{translate('pos.inventoryCategories.fields.name', 'Name')}</TableHead>
                  <TableHead>{translate('pos.inventoryCategories.fields.description', 'Description')}</TableHead>
                  <TableHead>{translate('pos.inventoryCategories.fields.isPackageRequired', 'Package Required')}</TableHead>
                  <TableHead>{translate('pos.inventoryCategories.fields.isActive', 'Active')}</TableHead>
                  <TableHead className="text-right">
                    {translate('common.actions', 'Actions')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCategories.map((category) => (
                  <TableRow key={category.id}>
                    <TableCell>
                      <div className="font-medium">{category.name}</div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-muted-foreground">
                        {category.description || '-'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        {category.isPackageRequired ? (
                          <>
                            <Package className="h-4 w-4 text-primary" />
                            <span className="text-sm">{translate('common.yes', 'Yes')}</span>
                          </>
                        ) : (
                          <>
                            <XCircle className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">{translate('common.no', 'No')}</span>
                          </>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        {category.isActive ? (
                          <>
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                            <span className="text-sm">{translate('common.active', 'Active')}</span>
                          </>
                        ) : (
                          <>
                            <XCircle className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">{translate('common.inactive', 'Inactive')}</span>
                          </>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end space-x-2">
                        {canEdit && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onEditClick(category)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        )}
                        {canDelete && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setCategoryToDelete(category);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {translate('pos.inventoryCategories.delete.title', 'Delete Category')}
            </DialogTitle>
            <DialogDescription>
              {translate(
                'pos.inventoryCategories.delete.description',
                'Are you sure you want to delete this category? This action cannot be undone.'
              )}
            </DialogDescription>
          </DialogHeader>
          {categoryToDelete && (
            <div className="py-4">
              <p className="font-medium">{categoryToDelete.name}</p>
              {categoryToDelete.description && (
                <p className="text-sm text-muted-foreground">
                  {categoryToDelete.description}
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setCategoryToDelete(null);
              }}
            >
              {translate('common.cancel', 'Cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              {translate('common.delete', 'Delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

